import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Mail,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  Star,
  Eye,
  Code,
  Clock,
  Globe,
  FileText,
  RefreshCw,
  Database,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  ShieldAlert,
  Wand2,
  ChevronDown,
  ChevronUp,
  History,
  RotateCcw,
  ArrowLeft,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  Switch,
  Textarea,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import {
  emailTemplatesApi,
  type EmailTemplate,
  type CreateEmailTemplateData,
  type UpdateEmailTemplateData,
  type TemplateType,
  type TemplateLanguage,
  type AIAnalysisResponse,
  type EmailTemplateVersion,
} from '@/api/endpoints/emailTemplates'
import { cn, formatDate } from '@/lib/utils'

// Language display names and flags
const LANGUAGE_INFO: Record<string, { name: string; flag: string }> = {
  en: { name: 'English', flag: '🇺🇸' },
  es: { name: 'Spanish', flag: '🇪🇸' },
  fr: { name: 'French', flag: '🇫🇷' },
  pt: { name: 'Portuguese', flag: '🇧🇷' },
}

// Template type colors
const getTypeColor = (type: string) => {
  switch (type) {
    case 'factoring_info':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'follow_up':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'meeting_confirmation':
      return 'bg-purple-100 text-purple-700 border-purple-200'
    case 'welcome':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200'
    case 'application_reminder':
      return 'bg-orange-100 text-orange-700 border-orange-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

const getTypeBadgeColor = (type: string) => {
  switch (type) {
    case 'factoring_info':
      return 'bg-blue-500'
    case 'follow_up':
      return 'bg-green-500'
    case 'meeting_confirmation':
      return 'bg-purple-500'
    case 'welcome':
      return 'bg-yellow-500'
    case 'application_reminder':
      return 'bg-orange-500'
    default:
      return 'bg-gray-500'
  }
}

// HTML Editor Component with Preview
function HtmlEditor({
  value,
  onChange,
  variables,
  previewVariables,
}: {
  value: string
  onChange: (value: string) => void
  variables?: Record<string, string>
  previewVariables?: Record<string, string>
}) {
  const [activeTab, setActiveTab] = useState<'code' | 'preview' | 'split'>('split')
  const [localPreviewVars, setLocalPreviewVars] = useState<Record<string, string>>(previewVariables || {})

  useEffect(() => {
    if (previewVariables) {
      setLocalPreviewVars(previewVariables)
    }
  }, [previewVariables])

  // Replace variables in HTML for preview
  const renderedHtml = useMemo(() => {
    let html = value
    Object.entries(localPreviewVars).forEach(([key, val]) => {
      // Replace {{variable}} syntax
      html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val)
      // Replace {{#variable}}content{{/variable}} conditional syntax
      html = html.replace(new RegExp(`\\{\\{#${key}\\}\\}([\\s\\S]*?)\\{\\{/${key}\\}\\}`, 'g'), val ? '$1' : '')
    })
    return html
  }, [value, localPreviewVars])

  return (
    <div className="space-y-3">
      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList className="grid w-[300px] grid-cols-3">
            <TabsTrigger value="code" className="flex items-center gap-1.5">
              <Code className="h-3.5 w-3.5" />
              Code
            </TabsTrigger>
            <TabsTrigger value="split" className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Split
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" />
              Preview
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {variables && Object.keys(variables).length > 0 && (
          <div className="text-xs text-muted-foreground">
            Variables: {Object.keys(variables).map(v => `{{${v}}}`).join(', ')}
          </div>
        )}
      </div>

      {/* Editor Content */}
      <div className={cn(
        'border rounded-lg overflow-hidden',
        activeTab === 'split' ? 'grid grid-cols-2 divide-x' : ''
      )}>
        {/* Code Editor */}
        {(activeTab === 'code' || activeTab === 'split') && (
          <div className="relative">
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={cn(
                'w-full font-mono text-sm p-4 resize-none focus:outline-none',
                activeTab === 'split' ? 'h-[400px]' : 'h-[500px]'
              )}
              placeholder="Enter HTML content..."
              spellCheck={false}
            />
            <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
              {value.length} chars
            </div>
          </div>
        )}

        {/* Preview */}
        {(activeTab === 'preview' || activeTab === 'split') && (
          <div className={cn(
            'bg-white overflow-auto',
            activeTab === 'split' ? 'h-[400px]' : 'h-[500px]'
          )}>
            <div
              className="p-4 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
          </div>
        )}
      </div>

      {/* Variable Preview Editor */}
      {variables && Object.keys(variables).length > 0 && (activeTab === 'preview' || activeTab === 'split') && (
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-xs font-medium mb-2">Preview Variables:</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(variables).map(([key, description]) => (
              <div key={key} className="flex items-center gap-2">
                <Label className="text-xs w-28 shrink-0">{key}:</Label>
                <Input
                  className="h-7 text-xs"
                  placeholder={description}
                  value={localPreviewVars[key] || ''}
                  onChange={(e) => setLocalPreviewVars(prev => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// AI Assistant Panel Component
function AIAssistantPanel({
  subject,
  bodyHtml,
  onApplySubject,
  onApplyBody,
}: {
  subject: string
  bodyHtml: string
  onApplySubject: (subject: string) => void
  onApplyBody: (html: string) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<AIAnalysisResponse | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)

  const runAnalysis = async (type: 'spam_check' | 'improve' | 'full') => {
    if (!subject || !bodyHtml) {
      setAnalysisError('Please enter both subject and body content first.')
      return
    }

    setIsAnalyzing(true)
    setAnalysisError(null)
    setIsExpanded(true)

    try {
      const response = await emailTemplatesApi.analyzeWithAI({
        subject,
        body_html: bodyHtml,
        analysis_type: type,
      })
      setAnalysisResult(response.data)
    } catch (error) {
      setAnalysisError('Failed to analyze template. Please try again.')
      console.error('AI analysis error:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const getSpamScoreColor = (score: number) => {
    if (score <= 30) return 'text-green-600 bg-green-100'
    if (score <= 60) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getSpamScoreLabel = (score: number) => {
    if (score <= 30) return 'Low Risk'
    if (score <= 60) return 'Medium Risk'
    return 'High Risk'
  }

  return (
    <div className="border rounded-lg bg-gradient-to-r from-purple-50 to-blue-50">
      {/* Header - Always visible */}
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <span className="font-medium text-purple-900">AI Assistant</span>
          <span className="text-xs text-muted-foreground">Spam checker & content optimizer</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => runAnalysis('spam_check')}
            disabled={isAnalyzing}
          >
            <ShieldAlert className="h-3.5 w-3.5 mr-1" />
            Check Spam
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => runAnalysis('improve')}
            disabled={isAnalyzing}
          >
            <Wand2 className="h-3.5 w-3.5 mr-1" />
            Improve
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs bg-purple-600 hover:bg-purple-700"
            onClick={() => runAnalysis('full')}
            disabled={isAnalyzing}
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" />
            {isAnalyzing ? 'Analyzing...' : 'Full Analysis'}
          </Button>
          {analysisResult && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {analysisError && (
        <div className="mx-3 mb-3 p-2 bg-red-100 text-red-700 rounded text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {analysisError}
        </div>
      )}

      {/* Analysis Results - Expandable */}
      {analysisResult && isExpanded && (
        <div className="px-3 pb-3 space-y-4 border-t border-purple-200 pt-3">
          {/* Spam Score */}
          {analysisResult.spam_score > 0 && (
            <div className="flex items-center gap-4">
              <div className={cn('px-3 py-2 rounded-lg font-bold', getSpamScoreColor(analysisResult.spam_score))}>
                Spam Score: {analysisResult.spam_score}/100
              </div>
              <span className={cn('text-sm font-medium', getSpamScoreColor(analysisResult.spam_score).split(' ')[0])}>
                {getSpamScoreLabel(analysisResult.spam_score)}
              </span>
            </div>
          )}

          {/* Summary */}
          <div className="p-3 bg-white rounded-lg">
            <p className="text-sm">{analysisResult.analysis_summary}</p>
          </div>

          {/* Spam Triggers */}
          {analysisResult.spam_triggers.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                <AlertTriangle className="h-4 w-4" />
                Spam Triggers Found
              </div>
              <div className="flex flex-wrap gap-1.5">
                {analysisResult.spam_triggers.map((trigger, i) => (
                  <Badge key={i} variant="outline" className="text-red-600 border-red-200 bg-red-50">
                    {trigger}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Subject Suggestions */}
          {analysisResult.subject_suggestions.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                <Lightbulb className="h-4 w-4" />
                Subject Line Suggestions
              </div>
              <div className="space-y-1.5">
                {analysisResult.subject_suggestions.map((suggestion, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-2 bg-white rounded border hover:bg-blue-50 cursor-pointer group"
                  >
                    <span className="text-sm">{suggestion}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onApplySubject(suggestion)
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Suggestions */}
          {analysisResult.content_suggestions.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                Content Improvements
              </div>
              <ul className="space-y-1 text-sm">
                {analysisResult.content_suggestions.map((suggestion, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-600 mt-1">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Deliverability Tips */}
          {analysisResult.deliverability_tips.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-purple-700">
                <Mail className="h-4 w-4" />
                Deliverability Tips
              </div>
              <ul className="space-y-1 text-sm">
                {analysisResult.deliverability_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-purple-600 mt-1">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Apply Improved Version */}
          {analysisResult.improved_subject && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 space-y-2">
              <p className="text-sm font-medium text-green-800">Improved Subject Line:</p>
              <div className="flex items-center justify-between">
                <code className="text-sm bg-white px-2 py-1 rounded">{analysisResult.improved_subject}</code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onApplySubject(analysisResult.improved_subject!)
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}

          {analysisResult.improved_body_html && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-green-800">AI-Improved Email Body Available</p>
                <Button
                  type="button"
                  size="sm"
                  className="h-7 text-xs bg-green-600 hover:bg-green-700"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onApplyBody(analysisResult.improved_body_html!)
                  }}
                >
                  Apply Improved Body
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function EmailTemplates() {
  const queryClient = useQueryClient()
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [languageFilter, setLanguageFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<EmailTemplateVersion | null>(null)
  const [duplicateTargetLanguage, setDuplicateTargetLanguage] = useState<string>('')
  const [versions, setVersions] = useState<EmailTemplateVersion[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [currentVersion, setCurrentVersion] = useState<string>('')
  const [formData, setFormData] = useState<CreateEmailTemplateData>({
    template_type: 'factoring_info',
    language: 'en',
    name: '',
    subject: '',
    body_html: '',
    body_text: '',
    description: '',
    is_default: false,
  })

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['email-templates', typeFilter, languageFilter],
    queryFn: () => emailTemplatesApi.getAll({
      template_type: typeFilter !== 'all' ? typeFilter : undefined,
      language: languageFilter !== 'all' ? languageFilter : undefined,
    }),
  })

  // Fetch template types and variables
  const { data: typesData } = useQuery({
    queryKey: ['email-template-types'],
    queryFn: () => emailTemplatesApi.getTypes(),
  })

  const templates = templatesData?.data?.templates || []
  const templateTypes: TemplateType[] = typesData?.data?.template_types || []
  const languages: TemplateLanguage[] = typesData?.data?.languages || []
  const availableVariables: Record<string, string> = typesData?.data?.variables || {}

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates
    const query = searchQuery.toLowerCase()
    return templates.filter(t =>
      t.name.toLowerCase().includes(query) ||
      t.subject.toLowerCase().includes(query) ||
      t.template_type.toLowerCase().includes(query)
    )
  }, [templates, searchQuery])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateEmailTemplateData) => emailTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setIsAddDialogOpen(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateEmailTemplateData }) =>
      emailTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setIsEditDialogOpen(false)
      setSelectedTemplate(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => emailTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setIsDeleteDialogOpen(false)
      setSelectedTemplate(null)
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id: number) => emailTemplatesApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: ({ id, targetLanguage }: { id: number; targetLanguage?: string }) =>
      emailTemplatesApi.duplicate(id, targetLanguage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setIsDuplicateDialogOpen(false)
      setSelectedTemplate(null)
      setDuplicateTargetLanguage('')
    },
  })

  // Migration and seed mutations
  const migrateMutation = useMutation({
    mutationFn: () => emailTemplatesApi.migrate(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })

  const seedMutation = useMutation({
    mutationFn: () => emailTemplatesApi.seed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
    },
  })

  const revertMutation = useMutation({
    mutationFn: ({ templateId, versionId }: { templateId: number; versionId: number }) =>
      emailTemplatesApi.revertToVersion(templateId, versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setIsVersionHistoryOpen(false)
      setSelectedVersion(null)
    },
  })

  const loadVersionHistory = async (templateId: number) => {
    setVersionsLoading(true)
    try {
      const response = await emailTemplatesApi.getVersions(templateId)
      setVersions(response.data.versions || [])
      setCurrentVersion(response.data.current_version || '')
    } catch (error) {
      console.error('Failed to load version history:', error)
      setVersions([])
    } finally {
      setVersionsLoading(false)
    }
  }

  const openVersionHistory = async (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setSelectedVersion(null)
    setIsVersionHistoryOpen(true)
    await loadVersionHistory(template.id)
  }

  const handleRevert = () => {
    if (!selectedTemplate || !selectedVersion) return
    revertMutation.mutate({
      templateId: selectedTemplate.id,
      versionId: selectedVersion.id,
    })
  }

  const handleMigrateAndSeed = async () => {
    try {
      await migrateMutation.mutateAsync()
      await seedMutation.mutateAsync()
    } catch {
      // Error handled by mutation
    }
  }

  const resetForm = () => {
    setFormData({
      template_type: 'factoring_info',
      language: 'en',
      name: '',
      subject: '',
      body_html: '',
      body_text: '',
      description: '',
      is_default: false,
    })
  }

  const handleCreate = () => {
    createMutation.mutate(formData)
  }

  const handleUpdate = () => {
    if (!selectedTemplate) return
    updateMutation.mutate({
      id: selectedTemplate.id,
      data: {
        name: formData.name,
        description: formData.description,
        subject: formData.subject,
        body_html: formData.body_html,
        body_text: formData.body_text,
        is_default: formData.is_default,
      },
    })
  }

  const handleDelete = () => {
    if (!selectedTemplate) return
    deleteMutation.mutate(selectedTemplate.id)
  }

  const openEditDialog = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setFormData({
      template_type: template.template_type,
      language: template.language,
      name: template.name,
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text || '',
      description: template.description || '',
      is_default: template.is_default,
    })
    setIsEditDialogOpen(true)
  }

  const openViewDialog = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setIsViewDialogOpen(true)
  }

  const openDeleteDialog = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setIsDeleteDialogOpen(true)
  }

  const openDuplicateDialog = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setDuplicateTargetLanguage('')
    setIsDuplicateDialogOpen(true)
  }

  const clearFilters = () => {
    setTypeFilter('all')
    setLanguageFilter('all')
    setSearchQuery('')
  }

  const hasActiveFilters = typeFilter !== 'all' || languageFilter !== 'all' || searchQuery

  // Get sample preview variables with values
  const samplePreviewVariables = useMemo(() => {
    const vars: Record<string, string> = {}
    Object.keys(availableVariables).forEach(key => {
      switch (key) {
        case 'contact_name':
          vars[key] = 'John Smith'
          break
        case 'company_name':
          vars[key] = 'ABC Trucking LLC'
          break
        case 'callback_number':
          vars[key] = '(555) 123-4567'
          break
        case 'agent_name':
          vars[key] = 'Sarah Johnson'
          break
        case 'agent_company':
          vars[key] = 'Riviera Finance'
          break
        case 'agent_email':
          vars[key] = 'sarah@rivierafinance.com'
          break
        default:
          vars[key] = `[${key}]`
      }
    })
    return vars
  }, [availableVariables])

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Email Templates</h1>
          <p className="text-muted-foreground">
            Manage email templates for follow-ups, confirmations, and outreach
          </p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length === 0 && (
            <Button
              variant="outline"
              onClick={handleMigrateAndSeed}
              disabled={migrateMutation.isPending || seedMutation.isPending}
            >
              {migrateMutation.isPending || seedMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Setup Database
                </>
              )}
            </Button>
          )}
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Template
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        {templateTypes.slice(0, 4).map(type => {
          const count = templates.filter(t => t.template_type === type.id).length
          const activeCount = templates.filter(t => t.template_type === type.id && t.is_active).length
          return (
            <Card key={type.id}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={cn('p-2 rounded-lg', getTypeColor(type.id))}>
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">
                      {type.name} ({activeCount} active)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Template Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {templateTypes.map(type => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Language Filter */}
            <Select value={languageFilter} onValueChange={setLanguageFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                {languages.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {LANGUAGE_INFO[lang.code]?.flag || '🌐'} {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {typeFilter !== 'all' && (
                <Badge variant="secondary">
                  {templateTypes.find(t => t.id === typeFilter)?.name || typeFilter}
                </Badge>
              )}
              {languageFilter !== 'all' && (
                <Badge variant="secondary">
                  {LANGUAGE_INFO[languageFilter]?.flag} {LANGUAGE_INFO[languageFilter]?.name || languageFilter}
                </Badge>
              )}
              {searchQuery && <Badge variant="secondary">Search: {searchQuery}</Badge>}
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Templates List */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            All Templates
            <Badge variant="outline" className="ml-2">{filteredTemplates.length} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex gap-4 animate-pulse p-4 border rounded-lg">
                  <div className="w-10 h-10 bg-muted rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/3" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No templates found</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Set up the database to load default templates or create your own'}
              </p>
              {!hasActiveFilters && templates.length === 0 && (
                <div className="flex items-center justify-center gap-3">
                  <Button
                    variant="default"
                    onClick={handleMigrateAndSeed}
                    disabled={migrateMutation.isPending || seedMutation.isPending}
                  >
                    {migrateMutation.isPending || seedMutation.isPending ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Loading default templates...
                      </>
                    ) : (
                      <>
                        <Database className="h-4 w-4 mr-2" />
                        Load Default Templates
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Custom
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => openViewDialog(template)}
                >
                  {/* Icon */}
                  <div className={cn('p-2 rounded-lg', getTypeColor(template.template_type))}>
                    <Mail className="h-5 w-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-medium">{template.name}</p>
                      <Badge className={cn('text-white text-xs', getTypeBadgeColor(template.template_type))}>
                        {template.template_type.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {LANGUAGE_INFO[template.language]?.flag} {template.language.toUpperCase()}
                      </Badge>
                      {template.is_default && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Default
                        </Badge>
                      )}
                      <Badge variant={template.is_active ? 'default' : 'secondary'}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    {/* Subject Preview */}
                    <p className="text-sm font-medium text-muted-foreground">
                      Subject: {template.subject}
                    </p>

                    {/* Description */}
                    {template.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                        {template.description}
                      </p>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        v{template.version}
                      </span>
                      <span>
                        Used {template.times_used} times
                      </span>
                      <span>Updated {formatDate(template.updated_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(template); }}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openDuplicateDialog(template); }}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openVersionHistory(template); }}>
                        <History className="h-4 w-4 mr-2" />
                        Version History
                      </DropdownMenuItem>
                      {!template.is_default && (
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDefaultMutation.mutate(template.id); }}>
                          <Star className="h-4 w-4 mr-2" />
                          Set as Default
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(template); }}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Template Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedTemplate && (
            <>
              <DialogHeader>
                <div className={cn(
                  'flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg text-white',
                  getTypeBadgeColor(selectedTemplate.template_type)
                )}>
                  <Mail className="h-6 w-6" />
                  <div>
                    <DialogTitle className="text-white">{selectedTemplate.name}</DialogTitle>
                    <p className="text-sm opacity-90">
                      {selectedTemplate.template_type.replace(/_/g, ' ')} - {LANGUAGE_INFO[selectedTemplate.language]?.name || selectedTemplate.language} - v{selectedTemplate.version}
                    </p>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {/* Status badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedTemplate.is_default && (
                    <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                      <Star className="h-3 w-3 mr-1 fill-current" />
                      Default
                    </Badge>
                  )}
                  <Badge variant={selectedTemplate.is_active ? 'default' : 'secondary'}>
                    {selectedTemplate.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Badge variant="outline">
                    <Globe className="h-3 w-3 mr-1" />
                    {LANGUAGE_INFO[selectedTemplate.language]?.flag} {selectedTemplate.language.toUpperCase()}
                  </Badge>
                </div>

                {/* Subject */}
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Subject</p>
                  <p className="font-medium">{selectedTemplate.subject}</p>
                </div>

                {/* Description */}
                {selectedTemplate.description && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Description</p>
                    <p className="text-sm">{selectedTemplate.description}</p>
                  </div>
                )}

                {/* HTML Preview */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Email Body Preview</p>
                  <div className="border rounded-lg overflow-hidden">
                    <div
                      className="p-4 bg-white prose prose-sm max-w-none min-h-[200px]"
                      dangerouslySetInnerHTML={{ __html: selectedTemplate.body_html }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">{selectedTemplate.times_used}</p>
                    <p className="text-xs text-muted-foreground">Times Used</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">v{selectedTemplate.version}</p>
                    <p className="text-xs text-muted-foreground">Version</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-lg font-bold">{formatDate(selectedTemplate.last_used_at || '-')}</p>
                    <p className="text-xs text-muted-foreground">Last Used</p>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex justify-between text-xs text-muted-foreground pt-4 border-t">
                  <span>Created: {formatDate(selectedTemplate.created_at)}</span>
                  <span>Updated: {formatDate(selectedTemplate.updated_at)}</span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => { setIsViewDialogOpen(false); openEditDialog(selectedTemplate); }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Template Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-green-600 text-white">
              <Plus className="h-6 w-6" />
              <DialogTitle className="text-white">Add New Email Template</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Template Type *</Label>
                <Select
                  value={formData.template_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, template_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templateTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">Language *</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, language: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {LANGUAGE_INFO[lang.code]?.flag} {lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 flex items-end">
                <div className="flex items-center gap-2 mb-1.5">
                  <Switch
                    id="is_default"
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                  />
                  <Label htmlFor="is_default">Set as Default</Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Factoring Info - English"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Brief description of when to use this template"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Email Subject *</Label>
              <Input
                id="subject"
                placeholder="e.g., Information about factoring services for {{company_name}}"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Email Body (HTML) *</Label>
              <HtmlEditor
                value={formData.body_html}
                onChange={(value) => setFormData(prev => ({ ...prev, body_html: value }))}
                variables={availableVariables}
                previewVariables={samplePreviewVariables}
              />
            </div>

            {/* AI Assistant */}
            <AIAssistantPanel
              subject={formData.subject}
              bodyHtml={formData.body_html}
              onApplySubject={(subject) => setFormData(prev => ({ ...prev, subject }))}
              onApplyBody={(html) => setFormData(prev => ({ ...prev, body_html: html }))}
            />

            <div className="space-y-2">
              <Label htmlFor="body_text">Plain Text Version (optional)</Label>
              <Textarea
                id="body_text"
                placeholder="Plain text version for email clients that don't support HTML"
                value={formData.body_text}
                onChange={(e) => setFormData(prev => ({ ...prev, body_text: e.target.value }))}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formData.name || !formData.subject || !formData.body_html || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <div className="flex items-center gap-3">
                <Pencil className="h-6 w-6" />
                <div>
                  <DialogTitle className="text-white">Edit Email Template</DialogTitle>
                  {selectedTemplate && (
                    <p className="text-sm opacity-90">v{selectedTemplate.version}</p>
                  )}
                </div>
              </div>
              {selectedTemplate && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => {
                    setIsEditDialogOpen(false)
                    openVersionHistory(selectedTemplate)
                  }}
                >
                  <History className="h-4 w-4 mr-2" />
                  View History
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Template Type</Label>
                <Input
                  value={formData.template_type.replace(/_/g, ' ')}
                  disabled
                  className="bg-muted capitalize"
                />
              </div>

              <div className="space-y-2">
                <Label>Language</Label>
                <Input
                  value={`${LANGUAGE_INFO[formData.language]?.flag || ''} ${LANGUAGE_INFO[formData.language]?.name || formData.language}`}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2 flex items-end">
                <div className="flex items-center gap-2 mb-1.5">
                  <Switch
                    id="edit-is_default"
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                  />
                  <Label htmlFor="edit-is_default">Set as Default</Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-name">Template Name *</Label>
              <Input
                id="edit-name"
                placeholder="e.g., Factoring Info - English"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                placeholder="Brief description of when to use this template"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-subject">Email Subject *</Label>
              <Input
                id="edit-subject"
                placeholder="e.g., Information about factoring services for {{company_name}}"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Email Body (HTML) *</Label>
              <HtmlEditor
                value={formData.body_html}
                onChange={(value) => setFormData(prev => ({ ...prev, body_html: value }))}
                variables={availableVariables}
                previewVariables={samplePreviewVariables}
              />
            </div>

            {/* AI Assistant */}
            <AIAssistantPanel
              subject={formData.subject}
              bodyHtml={formData.body_html}
              onApplySubject={(subject) => setFormData(prev => ({ ...prev, subject }))}
              onApplyBody={(html) => setFormData(prev => ({ ...prev, body_html: html }))}
            />

            <div className="space-y-2">
              <Label htmlFor="edit-body_text">Plain Text Version (optional)</Label>
              <Textarea
                id="edit-body_text"
                placeholder="Plain text version for email clients that don't support HTML"
                value={formData.body_text}
                onChange={(e) => setFormData(prev => ({ ...prev, body_text: e.target.value }))}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!formData.name || !formData.subject || !formData.body_html || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Template Dialog */}
      <Dialog open={isDuplicateDialogOpen} onOpenChange={setIsDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-purple-600 text-white">
              <Copy className="h-6 w-6" />
              <DialogTitle className="text-white">Duplicate Template</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p>Create a copy of this template, optionally for a different language.</p>
            {selectedTemplate && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedTemplate.name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedTemplate.template_type.replace(/_/g, ' ')} - {LANGUAGE_INFO[selectedTemplate.language]?.name}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="target-language">Target Language (optional)</Label>
              <Select
                value={duplicateTargetLanguage}
                onValueChange={setDuplicateTargetLanguage}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Same as original" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Same as original</SelectItem>
                  {languages.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {LANGUAGE_INFO[lang.code]?.flag} {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select a different language to create a translation variant
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedTemplate) {
                  duplicateMutation.mutate({
                    id: selectedTemplate.id,
                    targetLanguage: duplicateTargetLanguage || undefined,
                  })
                }
              }}
              disabled={duplicateMutation.isPending}
            >
              {duplicateMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Duplicating...
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-red-600 text-white">
              <Trash2 className="h-6 w-6" />
              <DialogTitle className="text-white">Delete Template</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p>Are you sure you want to delete this template?</p>
            {selectedTemplate && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedTemplate.name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedTemplate.template_type.replace(/_/g, ' ')} - {LANGUAGE_INFO[selectedTemplate.language]?.name}
                </p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The template will be soft-deleted and can be restored by an administrator.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={isVersionHistoryOpen} onOpenChange={setIsVersionHistoryOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between p-4 -m-6 mb-4 rounded-t-lg bg-indigo-600 text-white">
              <div className="flex items-center gap-3">
                <History className="h-6 w-6" />
                <div>
                  <DialogTitle className="text-white">Version History</DialogTitle>
                  {selectedTemplate && (
                    <p className="text-sm opacity-90">
                      {selectedTemplate.name} - Current: v{currentVersion}
                    </p>
                  )}
                </div>
              </div>
              {selectedVersion && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => setSelectedVersion(null)}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to List
                </Button>
              )}
            </div>
          </DialogHeader>

          {versionsLoading ? (
            <div className="py-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Loading version history...</p>
            </div>
          ) : selectedVersion ? (
            /* Version Detail View */
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-indigo-600">Version #{selectedVersion.version_number}</Badge>
                    <span className="text-sm text-muted-foreground">v{selectedVersion.version}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedVersion.change_summary || 'No change summary'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(selectedVersion.created_at)} by {selectedVersion.changed_by || 'system'}
                  </p>
                </div>
                <Button
                  onClick={handleRevert}
                  disabled={revertMutation.isPending}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {revertMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Reverting...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Revert to This Version
                    </>
                  )}
                </Button>
              </div>

              {/* Subject */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Subject</p>
                <p className="font-medium p-2 bg-muted rounded">{selectedVersion.subject}</p>
              </div>

              {/* Name */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Template Name</p>
                <p className="p-2 bg-muted rounded">{selectedVersion.name}</p>
              </div>

              {/* Body Preview */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Email Body Preview</p>
                <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <div
                    className="p-4 bg-white prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedVersion.body_html }}
                  />
                </div>
              </div>
            </div>
          ) : versions.length === 0 ? (
            <div className="py-12 text-center">
              <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No Version History</h3>
              <p className="text-muted-foreground">
                Version history will appear here after you make changes to this template.
              </p>
            </div>
          ) : (
            /* Version List View */
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {versions.length} previous version{versions.length !== 1 ? 's' : ''} available
              </p>
              {versions.map((version) => (
                <div
                  key={version.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedVersion(version)}
                >
                  <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700">
                    <History className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        Version #{version.version_number}
                      </Badge>
                      <span className="text-sm text-muted-foreground">v{version.version}</span>
                    </div>
                    <p className="font-medium text-sm">{version.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Subject: {version.subject}
                    </p>
                    {version.change_summary && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {version.change_summary}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(version.created_at)}</span>
                      {version.changed_by && (
                        <>
                          <span>by</span>
                          <span>{version.changed_by}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedVersion(version)
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsVersionHistoryOpen(false)
              setSelectedVersion(null)
            }}>
              Close
            </Button>
            {selectedTemplate && (
              <Button onClick={() => {
                setIsVersionHistoryOpen(false)
                setSelectedVersion(null)
                openEditDialog(selectedTemplate)
              }}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Current Version
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default EmailTemplates
